<template>
  <div
    :id="`typeahead-${id}`"
    :aria-owns="`result-list-${id}`"
    :aria-expanded="isFocused && data.length > 0 ? 'true' : 'false'"
    role="combobox"
    aria-haspopup="listbox"
    class="typeahead"
  >
    <div :class="inputGroupClasses">
      <div
        v-if="$slots.prepend || prepend"
        ref="prependDiv"
        class="input-group-prepend"
      >
        <slot name="prepend">
          <span class="input-group-text">{{ prepend }}</span>
        </slot>
      </div>
      <input
        ref="input"
        :id="`typeahead-input-${id}`"
        :class="[ 'form-control', inputClass, state !== null && { 'is-invalid': !state, 'is-valid': state } ]"
        :aria-labelledby="ariaLabelledBy"
        :aria-controls="`result-list-${id}`"
        :aria-activedescendant="`selected-option-${id}`"
        :name="inputName"
        :placeholder="placeholder"
        :aria-label="!ariaLabelledBy ? placeholder : false"
        :value="inputValue"
        :disabled="disabled"
        type="text"
        role="searchbox"
        aria-multiline="false"
        aria-autocomplete="list"
        autocomplete="off"
        @focus="handleFocus"
        @blur="handleFocusOut"
        @input="handleInput($event.target.value)"
        @keydown.esc="handleEsc($event.target.value)"
        @keydown="$emit('keydown', $event)"
        @keyup="$emit('keyup', $event)"
        @paste="$emit('paste', $event)"
      >
      <div
        v-if="$slots.append || append"
        class="input-group-append">
        <slot name="append">
          <span class="input-group-text">{{ append }}</span>
        </slot>
      </div>
    </div>
    <div
      v-if="loading"
      class="loading">
      <button>
        <i class="fas fa-spinner spin"/>
      </button>
    </div>
    <vue-bootstrap-autocomplete-list
      v-show="isFocused && (data.length > 0 || !!$scopedSlots.noResultsInfo || !!noResultsInfo)"
      ref="list"
      :id="`result-list-${id}`"
      :query="inputValue"
      :data="formattedData"
      :backgroundVariant="backgroundVariant"
      :backgroundVariantResolver="backgroundVariantResolver"
      :textVariant="textVariant"
      :maxMatches="maxMatches"
      :minMatchingChars="minMatchingChars"
      :noResultsInfo="noResultsInfo"
      :disableSort="disableSort"
      :showOnFocus="showOnFocus"
      :showAllResults="showAllResults"
      :highlightClass="highlightClass"
      :disabledValues="disabledValues"
      :vbtUniqueId="id"
      :showCreate="showCreate"
      class="vbt-autocomplete-list"
      role="listbox"
      @hit="handleHit"
      @listItemBlur="handleChildBlur"
    >
      <!-- pass down all scoped slots -->
      <template
        v-for="(slot, slotName) in $scopedSlots"
        slot-scope="{ data, htmlText }"
        :slot="slotName"
      >
        <slot
          :name="slotName"
          v-bind="{ data, htmlText }"/>
      </template>
      <!-- below is the right solution, however if the user does not provide a scoped slot, vue will still set $scopedSlots.suggestion to a blank scope
      <template v-if="$scopedSlots.suggestion" slot="suggestion" slot-scope="{ data, htmlText }">
        <slot name="suggestion" v-bind="{ data, htmlText }" />
      </template>-->
    </vue-bootstrap-autocomplete-list>
  </div>
</template>

<script>
import VueBootstrapAutocompleteList from './VueBootstrapAutocompleteList.vue'
import ResizeObserver from 'resize-observer-polyfill'

export default {
  name: 'VueBootstrapAutocomplete',

  components: {
    VueBootstrapAutocompleteList,
  },

  props: {
    ariaLabelledBy: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: null,
      validator: (size) => ['lg', 'md', 'sm'].indexOf(size) > -1,
    },
    value: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      required: true,
      validator: (d) => d instanceof Array,
    },
    serializer: {
      type: Function,
      default: (d) => d,
      validator: (d) => d instanceof Function,
    },
    // Don't call this method, use _screenReaderTextSerializer()
    // Using _screenReaderTextSerializer allows for defaulting based on .serializer
    screenReaderTextSerializer: {
      type: Function,
      validator: (d) => d instanceof Function,
    },
    backgroundVariant: String,
    backgroundVariantResolver: {
      type: Function,
      default: (d) => d,
      validator: (d) => d instanceof Function,
    },
    disabledValues: {
      type: Array,
      default: () => [],
    },
    textVariant: String,
    inputClass: {
      type: String,
      default: '',
    },
    inputName: {
      type: String,
      default: undefined,
    },
    maxMatches: {
      type: Number,
      default: 10,
    },
    minMatchingChars: {
      type: Number,
      default: 2,
    },
    disableSort: {
      type: Boolean,
      default: false,
    },
    noResultsInfo: {
      type: String,
    },
    showOnFocus: {
      type: Boolean,
      default: false,
    },
    showAllResults: {
      type: Boolean,
      default: false,
    },
    autoClose: {
      type: Boolean,
      default: true,
    },
    ieCloseFix: {
      type: Boolean,
      default: true,
    },
    placeholder: String,
    prepend: String,
    append: String,
    highlightClass: String,
    state: {
      type: Boolean,
      default: null,
    },
    loading: Boolean,
    showCreate: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['hit', 'input', 'keydown', 'keyup', 'focus', 'blur', 'inputBlur', 'paste'],

  computed: {
    id() {
      return Math.floor(Math.random() * 100000)
    },
    inputGroupClasses() {
      return this.size ? `input-group input-group-${this.size}` : 'input-group'
    },

    formattedData() {
      if (!(this.data instanceof Array)) {
        return []
      }
      return this.data.map((d, i) => {
        return {
          id: i,
          data: d,
          screenReaderText: this._screenReaderTextSerializer(d),
          text: this.serializer(d),
        }
      })
    },
  },

  methods: {
    _screenReaderTextSerializer(d) {
      if (typeof d === 'object' && !Array.isArray(d) && d !== null) {
        if (this.screenReaderTextSerializer) {
          return this.screenReaderTextSerializer(d)
        } else {
          return this.serializer(d)
        }
      } else {
        return d
      }
    },
    resizeList(el) {
      const rect = el.getBoundingClientRect()
      const listStyle = this.$refs.list.$el.style

      // Set the width of the list on resize
      listStyle.width = rect.width + 'px'

      // Set the margin when the prepend prop or slot is populated
      // (setting the "left" CSS property doesn't work)
      if (this.$refs.prependDiv) {
        const prependRect = this.$refs.prependDiv.getBoundingClientRect()
        listStyle.marginLeft = prependRect.width + 'px'
      }
    },

    handleHit(evt) {
      if (typeof this.value !== 'undefined') {
        this.$emit('input', evt.text)
      }

      this.inputValue = evt.text
      this.$emit('hit', evt.data)

      if (this.autoClose) {
        this.$refs.input.blur()
        this.isFocused = false
      }
    },

    handleChildBlur() {
      this.$refs.input.focus()
      this.isFocused = false
    },

    runFocusOut(evt) {
      setTimeout(() => {
        this.$emit('inputBlur', evt)
      }, 100)
      const tgt = evt.relatedTarget
      if (tgt && tgt.classList.contains('vbst-item')) {
        return
      }
      if (this.$refs.list.$el.matches(':hover')) {
        return
      }
      this.$emit('blur', evt)
      this.isFocused = false
    },

    handleFocusOut(evt) {
      if (!!navigator.userAgent.match(/Trident.*rv:11\./) && this.ieCloseFix) {
        setTimeout(() => {
          this.runFocusOut(evt)
        }, 300)
      } else {
        this.runFocusOut(evt)
      }
    },

    handleFocus() {
      this.$emit('focus')
      this.isFocused = true
    },

    handleInput(newValue) {
      this.isFocused = true
      this.inputValue = newValue

      // If v-model is being used, emit an input event
      if (typeof this.value !== 'undefined') {
        this.$emit('input', newValue)
      }
    },

    handleEsc(inputValue) {
      if (inputValue === '') {
        this.$refs.input.blur()
        this.isFocused = false
      } else {
        this.inputValue = ''
      }
    },
  },

  data() {
    return {
      isFocused: false,
      inputValue: this.value || '',
    }
  },

  mounted() {
    this.$_ro = new ResizeObserver((e) => {
      this.resizeList(this.$refs.input)
    })
    this.$_ro.observe(this.$refs.input)
    this.$_ro.observe(this.$refs.list.$el)
  },

  beforeDestroy() {
    this.$_ro.disconnect()
  },

  watch: {
    value: function (val) {
      this.inputValue = val
    },
  },
}
</script>

<style scoped>
.typeahead {
  position: relative;
}
.vbt-autocomplete-list {
  padding-top: 5px;
  position: absolute;
  max-height: 350px;
  -ms-overflow-style: -ms-autohiding-scrollbar;
  overflow-y: auto;
  z-index: 999;
}
.vbt-autocomplete-list >>> .vbt-matched-text {
  font-weight: bold;
}
.loading {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  z-index: 1000;
}

.loading button {
  height: 100%;
  padding: 0 15px;
  background: none;
  outline: none;
  border: none;
}
</style>



// WEBPACK FOOTER //
// src/lib/autocomplete/VueBootstrapAutocomplete.vue